"use client";

import { useSessionContext } from "../components/providers/session-provider";
import { sessionMonitoringService } from "../lib/services/session-monitoring-service";
import { useEffect, useRef, useState, useCallback } from "react";
import logger from "logger";

export interface UseSessionOptions {
  required?: boolean;
  onError?: (error: Error) => void;
  suspense?: boolean;
  // Grace period in ms before enforcing required redirects (optimistic access)
  gracePeriodMs?: number;
}

export interface UseSessionReturn {
  data: any | null;
  isLoading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  lastUpdated: Date;
  circuitBreakerState: any;
  retryCount: number;
  maxRetries: number;
  timeoutReached: boolean;
  fallbackUsed: boolean;
}

/**
 * Optimized useSession hook that uses centralized session management
 * with caching and request deduplication
 */
const SESSION_TIMEOUT_MS = 5000; // 5 seconds as per requirement 1.1

export function useOptimizedSession(
  options: UseSessionOptions = {},
): UseSessionReturn {
  const { required = false, onError, suspense = false } = options;

  // Early no-op on auth routes to prevent flicker and background checks
  if (typeof window !== "undefined") {
    const p = window.location.pathname;
    const isAuthRoute = [
      "/sign-in",
      "/sign-up",
      "/forgot-password",
      "/reset-password",
      "/set-password",
      "/activation-error",
      "/dealstudio/sign-in",
    ].some((r) => p === r || p.startsWith(r + "/"));

    if (isAuthRoute) {
      return {
        data: null,
        isLoading: false,
        error: null,
        refresh: async () => {},
        lastUpdated: new Date(0),
        circuitBreakerState: null,
        retryCount: 0,
        maxRetries: 3,
        timeoutReached: false,
        fallbackUsed: false,
      };
    }
  }

  const context = useSessionContext();

  // Handle both optimized and regular session context
  const session = context.session;
  const isLoading = context.isLoading;
  const error = context.error;
  const refresh = context.refresh;
  const lastUpdated = context.lastUpdated;

  // These properties might not exist in regular session context
  const circuitBreakerState =
    "circuitBreakerState" in context ? (context as any).circuitBreakerState : null;
  const retryCount = "retryCount" in context ? (context as any).retryCount : 0;
  const maxRetries = "maxRetries" in context ? (context as any).maxRetries : 3;

  // Enhanced timeout state management
  const [timeoutReached, setTimeoutReached] = useState(false);
  const [fallbackUsed, setFallbackUsed] = useState(false);
  const sessionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sessionLoadStartTime = useRef<number>(Date.now());

  // Track component usage for monitoring
  const componentRef = useRef<string | undefined>(undefined);

  // Enhanced logging for session loading states
  const logSessionState = useCallback(
    (message: string, details: any = {}) => {
      const sessionLoadTime = Date.now() - sessionLoadStartTime.current;
      const logData = {
        ...details,
        sessionLoadTime,
        isLoading,
        hasSession: !!session,
        component: componentRef.current || "useOptimizedSession",
      };

      logger.info(`[OptimizedSession] ${message}`, logData);
      sessionMonitoringService.logRequest({
        type: isLoading ? "cache_miss" : "cache_hit",
        responseTime: sessionLoadTime,
        component: componentRef.current || "useOptimizedSession",
        userId: session?.user?.id,
      });
    },
    [session, isLoading],
  );

  useEffect(() => {
    // Try to get component name from call stack (development only)
    if (process.env.NODE_ENV === "development") {
      const stack = new Error().stack;
      const match = stack?.match(/at (\w+)/g);
      if (match && match[1]) {
        componentRef.current = match[1].replace("at ", "");
      }
    }
  }, []);

  // Session timeout mechanism
  useEffect(() => {
    if (isLoading && !sessionTimeoutRef.current) {
      sessionLoadStartTime.current = Date.now();
      logSessionState("Session loading started, setting up timeout");

      sessionTimeoutRef.current = setTimeout(() => {
        const sessionLoadTime = Date.now() - sessionLoadStartTime.current;

        logSessionState("Session loading timeout reached", {
          sessionLoadTime,
          timeoutMs: SESSION_TIMEOUT_MS,
        });

        setTimeoutReached(true);
        setFallbackUsed(true);

        // Log timeout event for monitoring with better error details
        const timeoutError = new Error(
          `Session loading timeout after ${SESSION_TIMEOUT_MS}ms`,
        );

        // Some dev overlays and consoles stringify objects passed as separate args
        // and may display them as {}. Serialize explicitly for consistent visibility.
        const timeoutDetails = {
          sessionLoadTime,
          timeoutMs: SESSION_TIMEOUT_MS,
          component: componentRef.current || "useOptimizedSession",
          sessionData: !!session,
          userId: session?.user?.id,
        };
        try {
          console.error(
            `[OptimizedSession] Session timeout error: ${JSON.stringify(timeoutDetails)}`,
          );
        } catch {
          console.error("[OptimizedSession] Session timeout error (unserializable details)");
        }
        // Additionally log the details object for rich inspection in devtools
        if (process.env.NODE_ENV === "development") {
          console.error("[OptimizedSession] Timeout details", timeoutDetails);
        }

        sessionMonitoringService.logError(
          timeoutError,
          componentRef.current || "useOptimizedSession",
          session?.user?.id,
        );
      }, SESSION_TIMEOUT_MS);
    }

    // Clear timeout when loading completes
    if (!isLoading && sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
      sessionTimeoutRef.current = null;

      const sessionLoadTime = Date.now() - sessionLoadStartTime.current;
      logSessionState("Session loading completed", { sessionLoadTime });

      // Reset timeout state on successful load
      setTimeoutReached(false);
      setFallbackUsed(false);
    }

    // Cleanup timeout on unmount
    return () => {
      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current);
        sessionTimeoutRef.current = null;
      }
    };
  }, [isLoading, session, logSessionState]);

  // Handle error callback
  useEffect(() => {
    if (error && onError) {
      onError(error);
    }
  }, [error, onError]);

// Grace period before enforcing required session to allow optimistic render
const defaultGrace = 1200;
const graceMs = typeof options.gracePeriodMs === 'number' ? options.gracePeriodMs : defaultGrace;
const [gracePeriodOver, setGracePeriodOver] = useState(false);
useEffect(() => {
  const t = setTimeout(() => setGracePeriodOver(true), graceMs);
  return () => clearTimeout(t);
}, [graceMs]);

// Handle required session after grace period or after we have a definitive update
useEffect(() => {
  const hasDefinitiveResult = lastUpdated && lastUpdated.getTime() > 0;
  if (required && (gracePeriodOver || hasDefinitiveResult) && !session && !error) {
    const redirectError = new Error("Session required but not found");
    if (onError) {
      onError(redirectError);
    } else {
      // Default behavior: redirect to sign-in
      if (typeof window !== "undefined") {
        window.location.href = "/sign-in";
      }
    }
  }
}, [required, session, error, onError, gracePeriodOver, lastUpdated]);

  // Suspense support (experimental)
  if (suspense && isLoading && !session) {
    throw new Promise((resolve) => {
      const checkSession = () => {
        if (!isLoading || session) {
          resolve(session);
        } else {
          setTimeout(checkSession, 100);
        }
      };
      checkSession();
    });
  }

  return {
    data: session,
    isLoading,
    error,
    refresh,
    lastUpdated,
    circuitBreakerState,
    retryCount: retryCount as number,
    maxRetries: maxRetries as number,
    timeoutReached,
    fallbackUsed,
  };
}

/**
 * Backward compatibility hook that matches better-auth's useSession API
 */
export function useSession(options: UseSessionOptions = {}) {
  const result = useOptimizedSession(options);

  // Match better-auth's API structure
  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error,
    refresh: result.refresh,
    lastUpdated: result.lastUpdated,
    circuitBreakerState: result.circuitBreakerState,
    retryCount: result.retryCount,
    maxRetries: result.maxRetries,
    timeoutReached: result.timeoutReached,
    fallbackUsed: result.fallbackUsed,
  };
}
